import { Feature } from 'src/app/modules/feature-toggle/enums/features.enum';

/**
 * C1 — Sechs Themen statt sechs Sätze.
 *
 * Im Backend liegen **82 Werkzeuge**, rund ein Drittel davon schreibend. Sichtbar waren
 * davon sechs Beispielsätze im leeren Zustand und drei Chips danach — großzügig gerechnet
 * **neun von 82**.
 *
 * Mehr Beispiele lösen das nicht: Sechs sind für einen Telefonbildschirm schon zu viel, und
 * 82 Werkzeuge lassen sich nicht auflisten. Sechs Themen sind die Landkarte — Umfang auf
 * einen Blick, Richtung mit einem Tipp. Die Beispiele darunter wechseln mit der Auswahl.
 *
 * ---
 *
 * ## Die zwei Fallen dieser Datei
 *
 * **1 · Sie ist eine ZWEITE Quelle neben dem Werkzeugkatalog und driftet.** Wer im Backend
 * ein Werkzeug ergänzt, ergänzt hier nichts — und niemand merkt es. Deshalb steht unter den
 * Themen „Alles ansehen, was ich kann": Das ruft `get_capabilities`, und der Assistent
 * beantwortet die Frage nach seinem Umfang **selbst**. Diese Liste ist die Abkürzung, nicht
 * die Wahrheit.
 *
 * **2 · Ein Thema, das der Nutzer nicht sehen darf, ist ein totes Versprechen.** Die
 * Registry im Backend filtert Werkzeuge bereits nach Berechtigung; das Frontend braucht
 * dieselbe Trennung, sonst bietet es „Zeiten" an, wo es keine Zeiterfassung gibt. Deshalb
 * trägt jedes Thema seine Feature-Voraussetzung ({@link AssistantTopic.requiresFeature}).
 *
 * ## ⚠️ Und die Falle, die schon einmal zugeschlagen hat
 *
 * Jeder Beispielsatz muss den **Werkzeugkatalog** treffen. Am 04.09.2026 waren 15 von 30
 * Demo-Sätzen tot, weil sie als Verkaufstext formuliert waren („Wochenendspitze"), wo der
 * Katalog „automatisch füllen" kennt. Die Sätze hier sind deshalb an den
 * `ExampleUtterances` der Werkzeuge ausgerichtet — nachgelesen am 09.09.2026 mit
 * `_local-analysis/misc/katalog-wortschatz-lesen.py`, gegengeprüft mit
 * `demo-assistent-vorschlaege-pruefen.py`.
 */

/** Ein Thema der Landkarte. */
export interface AssistantTopic {
  /** Stabiler Schlüssel (auch die Zuordnung der Beispiele hängt daran). */
  id: AssistantTopicId;
  /** i18n-Key der Beschriftung — NIE ein deutsches Literal (siehe E1). */
  labelKey: string;
  icon: string;
  /**
   * Ohne dieses Feature erscheint das Thema nicht.
   * `undefined` = immer sichtbar (Planen und Besetzen sind der Kern des Produkts).
   */
  requiresFeature?: Feature;
  /**
   * Beispielsätze dieses Themas — Schlüssel relativ zu `HELP_CHAT.SUGGESTED.`.
   * Jeder trifft ein Werkzeug; die Herkunft steht im Kommentar daneben.
   */
  askKeys: readonly string[];
  doKeys: readonly string[];
}

export type AssistantTopicId =
  | 'plan'
  | 'staff'
  | 'time'
  | 'absence'
  | 'analyze'
  | 'risk';

export const ASSISTANT_TOPICS: readonly AssistantTopic[] = [
  {
    id: 'plan',
    labelKey: 'HELP_CHAT.TOPIC.PLAN',
    icon: 'fa-solid fa-calendar-days',
    // Planen ist der Kern — ohne Schichtplanung gäbe es das Produkt nicht.
    askKeys: ['ASK_COVERAGE'],
    doKeys: ['DO_AUTOFILL', 'DO_PUBLISH'],
  },
  {
    id: 'staff',
    labelKey: 'HELP_CHAT.TOPIC.STAFF',
    icon: 'fa-solid fa-user-group',
    askKeys: ['ASK_WHO_WORKS', 'ASK_GAPS'],
    doKeys: ['DO_REPLACEMENT', 'DO_PLAN'],
  },
  {
    id: 'time',
    labelKey: 'HELP_CHAT.TOPIC.TIME',
    icon: 'fa-solid fa-clock',
    requiresFeature: Feature.TIME_TRACKING_BASIC,
    askKeys: ['ASK_LIVE', 'ASK_CLOCK'],
    doKeys: [],
  },
  {
    id: 'absence',
    labelKey: 'HELP_CHAT.TOPIC.ABSENCE',
    icon: 'fa-solid fa-umbrella-beach',
    requiresFeature: Feature.ABSENCE_MANAGEMENT_BASIC,
    askKeys: ['ASK_ON_VACATION', 'ASK_VACATION'],
    doKeys: ['DO_SICK', 'DO_VACATION'],
  },
  {
    id: 'analyze',
    labelKey: 'HELP_CHAT.TOPIC.ANALYZE',
    icon: 'fa-solid fa-chart-simple',
    askKeys: ['ASK_FAIRNESS', 'ASK_BRIEFING'],
    doKeys: [],
  },
  {
    id: 'risk',
    labelKey: 'HELP_CHAT.TOPIC.RISK',
    icon: 'fa-solid fa-triangle-exclamation',
    askKeys: ['ASK_REST_PERIODS', 'ASK_ALERTS'],
    doKeys: [],
  },
];

/**
 * Der Satz hinter „Alles ansehen, was ich kann".
 *
 * Wörtlich ein `ExampleUtterance` von `GetCapabilitiesTool` — damit trifft er den Router
 * ohne Umweg, und der Assistent zählt seine Fähigkeiten selbst auf. Der einzige Eintrag
 * dieser Datei, der nicht driften kann.
 */
export const CAPABILITIES_QUESTION_KEY = 'HELP_CHAT.SUGGESTED.ASK_CAPABILITIES';
